import type { ClinicianLabFeedback } from "@care-by-ella/contracts";
import { analyteName } from "./lab-labels";

// TES-1370 (Ö-582) — the doctor's review of the patient texts for one lab
// order, as pure functions so the panel and its tests read the same rules.
//
// Emelie: "Behörig läkare granskar, ändrar och godkänner uttryckligen allt
// före publicering." The server enforces it (a publish with anything
// unapproved is refused); these functions only make the state visible and
// keep the doctor from pressing a button the server will refuse.

export type FeedbackItemState =
  // Nothing written: a value without an explanation needs no approval.
  | "empty"
  // Approved exactly as it stands.
  | "approved"
  // Approved, then edited here: the approval no longer covers the text.
  | "edited_since_approval"
  // Written (or edited) and not approved.
  | "unapproved";

export function feedbackItemState(item: {
  savedText: string;
  approvedAt: string | null | undefined;
  localText: string;
}): FeedbackItemState {
  if (!item.localText.trim()) return "empty";
  if (item.localText !== item.savedText) {
    return item.approvedAt ? "edited_since_approval" : "unapproved";
  }
  return item.approvedAt ? "approved" : "unapproved";
}

export const FEEDBACK_ITEM_STATE_LABEL: Record<FeedbackItemState, string> = {
  empty: "Ingen kommentar",
  approved: "Godkänd",
  edited_since_approval: "Ändrad efter godkännande",
  unapproved: "Inte godkänd",
};

// An order sent before TES-1370: the patient reads its live columns, so it
// is shown, never edited or published again here.
export function isLegacySent(fb: ClinicianLabFeedback): boolean {
  return fb.feedbackSentAt !== null && !fb.publishedAt;
}

export interface PublishReadiness {
  ready: boolean;
  // Why not, in the doctor's words. Null when ready.
  reason: string | null;
  // Local edits the server has not seen.
  dirty: boolean;
}

export function publishReadiness(
  fb: ClinicianLabFeedback,
  local: { summary: string; comments: Record<string, string> },
): PublishReadiness {
  const dirty =
    local.summary !== fb.patientSummary ||
    fb.results.some((r) => (local.comments[r.analyteKey] ?? "") !== r.comment);
  if (isLegacySent(fb)) {
    return {
      ready: false,
      reason: "Det här provsvaret skickades före granskningsflödet.",
      dirty,
    };
  }
  if (dirty) {
    return {
      ready: false,
      reason: "Spara eller godkänn dina ändringar innan du publicerar.",
      dirty,
    };
  }
  if (!fb.patientSummary.trim()) {
    return {
      ready: false,
      reason: "Skriv och godkänn en sammanfattning av provtagningen.",
      dirty,
    };
  }
  if (!fb.summaryApprovedAt) {
    return { ready: false, reason: "Godkänn sammanfattningen.", dirty };
  }
  const unapproved = fb.results
    .filter((r) => r.comment.trim() && !r.commentApprovedAt)
    .map((r) => analyteName(r.analyteKey));
  if (unapproved.length > 0) {
    return {
      ready: false,
      reason: `Godkänn kommentaren till ${unapproved.join(", ")}.`,
      dirty,
    };
  }
  return { ready: true, reason: null, dirty };
}

// "Delsvar ska markeras tydligt som ofullständiga" (Ö-582, Ö-583).
export function completenessLabel(fb: ClinicianLabFeedback): {
  partial: boolean;
  text: string;
} {
  const missing = fb.missingAnalyteKeys ?? [];
  if (fb.orderStatus === "partial") {
    return {
      partial: true,
      text:
        missing.length > 0
          ? `Delsvar – ${missing.length} ${missing.length === 1 ? "värde saknas" : "värden saknas"}: ${missing.map(analyteName).join(", ")}`
          : "Delsvar – labbet har inte skickat alla svar",
    };
  }
  return { partial: false, text: "Komplett svar" };
}

// What the patient currently has of one value.
export type ValuePublishState =
  // Not published yet (never, or the lab changed it after the last publish).
  | "unpublished"
  // Published, and the explanation she reads is the one written here.
  | "published"
  // Published, but the explanation has been edited since.
  | "changed_since_publish";

export function valuePublishState(r: {
  comment: string;
  publishedAt?: string | null;
  publishedComment?: string | null;
}): ValuePublishState {
  if (!r.publishedAt) return "unpublished";
  return (r.publishedComment ?? "") === r.comment.trim()
    ? "published"
    : "changed_since_publish";
}

export const VALUE_PUBLISH_STATE_LABEL: Record<ValuePublishState, string> = {
  unpublished: "Inte publicerad",
  published: "Publicerad",
  changed_since_publish: "Ändrad sedan publicering",
};

// The draft's provenance, shown above every draft text.
export function draftLabel(fb: ClinicianLabFeedback): string {
  if (fb.draftReviewStatus === "qa_working_assumption_pending_kirill") {
    return "Utkast från mall – arbetsantagande, väntar på Kirills granskning";
  }
  return `Utkast (${fb.aiDraftGeneratorVersion ?? "okänd version"})`;
}
